import { isVisibleAiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'

type ClientWithId = {
  id: string
}

export type AiUnreadClientActivity<T extends ClientWithId> = {
  client: T
  unreadCount: number
  conversationId: string
}

type AiUnreadAggregate = {
  unreadCount: number
  mostRecentConversation: ConversationObject
}

const getConversationTimestamp = (conversation: ConversationObject): number => {
  const timestamp =
    conversation.conversation_last_message_sent_datetime ??
    conversation.conversation_update_datetime ??
    conversation.conversation_date_created
  const parsedTimestamp = Date.parse(timestamp)
  return Number.isNaN(parsedTimestamp) ? Number.NEGATIVE_INFINITY : parsedTimestamp
}

const isMoreRecentConversation = (candidate: ConversationObject, current: ConversationObject): boolean => {
  const candidateTimestamp = getConversationTimestamp(candidate)
  const currentTimestamp = getConversationTimestamp(current)

  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp
  }

  // Keep the result deterministic when timestamps are equal or invalid.
  return candidate.conversation_id.localeCompare(current.conversation_id) > 0
}

export const getAiUnreadClientActivities = <T extends ClientWithId>(
  clients: readonly T[],
  conversations: readonly ConversationObject[],
): AiUnreadClientActivity<T>[] => {
  const clientIds = new Set(clients.map((client) => client.id))
  const aggregateByClientId = new Map<string, AiUnreadAggregate>()

  conversations.forEach((conversation) => {
    const unreadCount = conversation.conversation_attributes?.unread_ai_coach_messages_provider_count ?? 0
    if (!clientIds.has(conversation.patient_lyra_id) || !isVisibleAiConversation(conversation) || unreadCount <= 0) {
      return
    }

    const currentAggregate = aggregateByClientId.get(conversation.patient_lyra_id)
    aggregateByClientId.set(conversation.patient_lyra_id, {
      unreadCount: (currentAggregate?.unreadCount ?? 0) + unreadCount,
      mostRecentConversation:
        !currentAggregate || isMoreRecentConversation(conversation, currentAggregate.mostRecentConversation)
          ? conversation
          : currentAggregate.mostRecentConversation,
    })
  })

  return clients.flatMap((client) => {
    const aggregate = aggregateByClientId.get(client.id)
    return aggregate
      ? [
          {
            client,
            unreadCount: aggregate.unreadCount,
            conversationId: aggregate.mostRecentConversation.conversation_id,
          },
        ]
      : []
  })
}
